import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on app load
    const savedUser = localStorage.getItem('basketball-recruiting-user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    
    // Simulate API call - in real app, this would call your backend
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Only allow specific credentials
    if (
        (email === 'david.keyes@zoneai.org' && password === 'Kentfan21!') ||
        (email === 'jrydquist@usfca.edu' && password === 'GoDons!')
      ) {

        const nameMap: Record<string, string> = {
          'david.keyes@zoneai.org': 'David Keyes',
          'jrydquist@usfca.edu': 'Jack Rydquist',
        };

        const idMap: Record<string, string> = {
          'david.keyes@zoneai.org': '1',
          'jrydquist@usfca.edu': '2',
        };

        const newUser: User = {
          id: idMap[email] || '0',
          email: email,
          name: nameMap[email] || email,
        };
      
      setUser(newUser);
      localStorage.setItem('basketball-recruiting-user', JSON.stringify(newUser));
      setIsLoading(false);
      return true;
    }
    
    setIsLoading(false);
    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('basketball-recruiting-user');
  };

  const value = {
    user,
    login,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
